// Core
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import { Button, Chip, ConfirmDialog, MultiSelect, SectionCard } from "components/AtomsV2";
import { Icon } from "components/ShellV2";

// Redux
import { useDispatch, useSelector } from "react-redux";
import { openNotificationPopup, selectTimeSelector } from "state/application/applicationSlice";
import {
  useDownloadBillingGuideByCommunityIdFileNameMutation,
  useDeleteBillingGuideByCommunityIdFileNameMutation,
  useGetBillingGuidesByCommunityIdQuery,
  useGenerateExcelBillingGuideMutation,
  useGeneratePdfBillingGuideMutation,
} from "state/sessions/settlementApiSlice";
import { useGetMembersByCommunityIdQuery, selectAllMembers } from "state/members/membersApiSlice";
import { useGetTaskStatusByIdQuery } from "state/tasks/tasksApiSlice";
import { addTask, selectActiveTasksByType } from "state/tasks/tasksSlice";

// Utils
import { useGetCommunity } from "utils/crud/read";
import useTaskStatusEffect from "utils/hooks/useTaskStatusEffect";
import { formatLocalDateTime } from "utils/time";

const TASK_TYPE = "UPDATE_BILLING_GUIDES";

const SHEET_OPTIONS = [
  { value: "member_total", label: "Member | Total", distinct: false },
  { value: "member_resource_total", label: "Member-Resource | Total", distinct: false },
  { value: "member_resource_distinct", label: "Member-Resource | Distinct", distinct: true },
  { value: "transactions_total", label: "Transactions | Total", distinct: false },
  { value: "transactions_distinct", label: "Transactions | Distinct", distinct: true },
  { value: "rec_total", label: "REC Total", distinct: false },
];

function fileMeta(name) {
  const ext = (name?.split(".").pop() || "").toLowerCase();
  const isPdf = ext === "pdf";
  return {
    isPdf,
    label: isPdf ? "PDF" : "Excel",
    icon: isPdf ? "picture_as_pdf" : "table_view",
    cls: isPdf ? "rec-tint-danger" : "rec-tint-green",
    tone: isPdf ? "red" : "green",
  };
}

function BillingGuide() {
  const community = useGetCommunity();
  const dispatch = useDispatch();
  const activeLoadsTasks = useSelector(selectActiveTasksByType(TASK_TYPE));
  const { startDate, endDate } = useSelector(selectTimeSelector);

  const { t, i18n } = useTranslation(["settlement", "common"]);

  const [page, setPage] = useState(1);
  const pageSize = 10;

  // Selected users state
  const [selectedUsers, setSelectedUsers] = useState([]);

  // Selected sheets state (default to non-distinct options)
  const [selectedSheets, setSelectedSheets] = useState([
    "member_total",
    "member_resource_total",
    "transactions_total",
    "rec_total",
  ]);

  // Get members
  const { data: membersData } = useGetMembersByCommunityIdQuery({
    communityId: community.id,
    excludeDeleted: true,
  });
  const members = useMemo(() => (membersData ? selectAllMembers(membersData) : []), [membersData]);

  // Initialize selected users to all members
  useEffect(() => {
    if (members.length > 0 && selectedUsers.length === 0) {
      setSelectedUsers(members.map((m) => m.id));
    }
  }, [members]);

  const memberOptions = useMemo(
    () => members.map((m) => ({ value: m.id, label: m.name || m.email })),
    [members]
  );

  const hasDistinctSheets = useMemo(
    () => selectedSheets.some((sheet) => SHEET_OPTIONS.find((o) => o.value === sheet)?.distinct),
    [selectedSheets]
  );

  // Read Billing Guides
  const { data: billingGuides, refetch } = useGetBillingGuidesByCommunityIdQuery(community.id, {
    skip: !community?.id,
  });

  // Generate Excel Billing Guide
  const [generateExcel, { isLoading: isGeneratingExcel }] = useGenerateExcelBillingGuideMutation();
  // Generate PDF Billing Guide
  const [generatePdf, { isLoading: isGeneratingPdf }] = useGeneratePdfBillingGuideMutation();

  const generate = async (kind) => {
    try {
      const selectedLanguage = (i18n.language || "en").split("-")[0].toLowerCase();
      const language = ["en", "pt", "es"].includes(selectedLanguage) ? selectedLanguage : "en";

      // Convert sheet values to labels for backend
      const sheetLabels = selectedSheets
        .map((value) => SHEET_OPTIONS.find((opt) => opt.value === value)?.label)
        .filter(Boolean);

      const payload = {
        communityId: community.id,
        startDate,
        endDate,
        userIds: selectedUsers,
        sheets: sheetLabels,
        language,
      };

      const mutation = kind === "excel" ? generateExcel : generatePdf;
      const response = await mutation(payload).unwrap();

      if (response.task_id) {
        dispatch(addTask({ id: response.task_id, type: TASK_TYPE }));
        return dispatch(
          openNotificationPopup({
            notification: kind === "excel" ? "Excel generation started" : "PDF generation started",
            type: "success",
          })
        );
      }

      return dispatch(
        openNotificationPopup({
          notification: response.message || "Generation completed",
          type: "success",
        })
      );
    } catch (err) {
      return dispatch(openNotificationPopup({ notification: err, type: "error" }));
    }
  };

  // Download Billing Guide
  const [downloadBillingGuide] = useDownloadBillingGuideByCommunityIdFileNameMutation();
  const onDownloadBillingGuide = async (fileName) => {
    try {
      const result = await downloadBillingGuide({ communityId: community.id, fileName }).unwrap();
      const hiddenElement = document.createElement("a");
      const url = window.URL || window.webkitURL;
      const blobPDF = url.createObjectURL(result);
      hiddenElement.href = blobPDF;
      hiddenElement.target = "_blank";
      hiddenElement.download = `${fileName}`;
      hiddenElement.click();
      return { data: null };
    } catch (err) {
      return dispatch(openNotificationPopup({ notification: err, type: "error" }));
    }
  };

  // Delete Billing Guide
  const [deleteBillingGuide] = useDeleteBillingGuideByCommunityIdFileNameMutation();
  const [del, setDel] = useState(null);
  const onDeleteBillingGuide = async (fileName) => {
    try {
      await deleteBillingGuide({ communityId: community.id, fileName }).unwrap();
      return dispatch(
        openNotificationPopup({
          notification: "File deleted successfully",
          type: "success",
        })
      );
    } catch (err) {
      return dispatch(openNotificationPopup({ notification: err, type: "error" }));
    }
  };

  // GET Task Status
  const {
    data: taskStatus,
    isFetching,
    isSuccess,
  } = useGetTaskStatusByIdQuery(
    { taskId: activeLoadsTasks[0]?.id, communityId: community.id },
    {
      pollingInterval: 3000,
      refetchOnFocus: true,
      refetchOnMountOrArgChange: true,
      skip: activeLoadsTasks.length === 0,
    }
  );
  useTaskStatusEffect(
    { isSuccess, isFetching, taskStatus, activeLoadsTasks, dispatch, TASK_TYPE },
    refetch
  );

  const sortedBillingGuides = useMemo(
    () => (Array.isArray(billingGuides) ? billingGuides : []),
    [billingGuides]
  );

  const totalPages = useMemo(
    () => Math.ceil(sortedBillingGuides.length / pageSize) || 1,
    [sortedBillingGuides.length]
  );
  const currentPageItems = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sortedBillingGuides.slice(start, start + pageSize);
  }, [sortedBillingGuides, page]);

  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [totalPages]);

  const canGen =
    selectedSheets.length > 0 && selectedUsers.length > 0 && !isGeneratingExcel && !isGeneratingPdf;

  const when = (iso) => formatLocalDateTime(iso, "");

  return (
    <div className="flex flex-col gap-rec">
      <SectionCard icon="receipt_long" title={t("files.title")} sub={t("files.caption")}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div className="rec-field">
            <label htmlFor="bg-members">{t("common:member_other")}</label>
            <MultiSelect
              options={memberOptions}
              selected={selectedUsers}
              onChange={setSelectedUsers}
              selectAll
              selectAllLabel={t("common:selectall")}
              placeholder={t("common:selectall")}
              countNoun={t("common:member_other")}
            />
          </div>
          <div className="rec-field">
            <label htmlFor="bg-sheets">{t("files.sheets")}</label>
            <MultiSelect
              options={SHEET_OPTIONS}
              selected={selectedSheets}
              onChange={setSelectedSheets}
              placeholder={t("common:selectall")}
              countNoun={t("files.sheets")}
            />
            {hasDistinctSheets && (
              <small className="rec-field-err mt-1" style={{ color: "var(--warning)" }}>
                <Icon name="warning" style={{ fontSize: 14 }} />
                {t("files.distinctwarning")}
              </small>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-[10px]">
          <Button disabled={!canGen} onClick={() => generate("excel")}>
            {isGeneratingExcel ? (
              <>
                <span className="animate-spin inline-flex">
                  <Icon name="progress_activity" />
                </span>
                {t("files.generateexcel")}
              </>
            ) : (
              <>
                <Icon name="table_view" />
                {t("files.generateexcel")}
              </>
            )}
          </Button>
          <Button disabled={!canGen} onClick={() => generate("pdf")}>
            {isGeneratingPdf ? (
              <>
                <span className="animate-spin inline-flex">
                  <Icon name="progress_activity" />
                </span>
                {t("files.generatepdf")}
              </>
            ) : (
              <>
                <Icon name="picture_as_pdf" />
                {t("files.generatepdf")}
              </>
            )}
          </Button>
        </div>
      </SectionCard>

      <SectionCard
        icon="folder"
        title={t("files.existingfiles")}
        sub={`${sortedBillingGuides.length}`}
      >
        {currentPageItems.length === 0 ? (
          <div className="py-10 text-center text-ink-3 text-[13px]">{t("files.empty")}</div>
        ) : (
          <div className="flex flex-col">
            {currentPageItems.map((bg) => {
              const m = fileMeta(bg.name);
              return (
                <div
                  key={bg.name}
                  className="flex items-center gap-3 py-3 border-b border-[var(--border)] last:border-0"
                >
                  <div className={`rec-kpi-ic ${m.cls} shrink-0`}>
                    <Icon name={m.icon} fill />
                  </div>
                  <div className="flex-1 min-w-0">
                    <b className="block truncate text-[13.5px] text-ink">{bg.name}</b>
                    <small className="text-ink-3 text-[12px]">
                      {m.label}
                      {bg.creation_time ? ` · ${when(bg.creation_time)}` : ""}
                    </small>
                  </div>
                  <Chip tone={m.tone}>{m.label}</Chip>
                  <button
                    type="button"
                    className="rec-icon-btn"
                    title={t("files.download", { defaultValue: "Transferir" })}
                    onClick={() => onDownloadBillingGuide(bg.name)}
                  >
                    <Icon name="download" />
                  </button>
                  <button
                    type="button"
                    className="rec-icon-btn danger"
                    title={t("files.delete", { defaultValue: "Apagar" })}
                    onClick={() => setDel(bg)}
                  >
                    <Icon name="delete" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
        {sortedBillingGuides.length > pageSize && (
          <div className="rec-pager justify-center mt-4">
            <button
              type="button"
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <Icon name="chevron_left" style={{ fontSize: 18 }} />
            </button>
            {Array.from({ length: totalPages }).map((_, idx) => {
              const p = idx + 1;
              return (
                <button
                  // eslint-disable-next-line react/no-array-index-key
                  key={p}
                  type="button"
                  className={p === page ? "on" : ""}
                  onClick={() => setPage(p)}
                >
                  {p}
                </button>
              );
            })}
            <button
              type="button"
              disabled={page === totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              <Icon name="chevron_right" style={{ fontSize: 18 }} />
            </button>
          </div>
        )}
      </SectionCard>

      <ConfirmDialog
        open={!!del}
        danger
        icon="delete"
        title={t("common:confirm_modal.title")}
        body={t("common:confirm_modal.irreversible")}
        confirmLabel={t("common:confirm")}
        cancelLabel={t("common:cancel")}
        onCancel={() => setDel(null)}
        onConfirm={() => {
          onDeleteBillingGuide(del.name);
          setDel(null);
        }}
      />
    </div>
  );
}

export default BillingGuide;
